<script lang="ts">
	/**
	 * One test-mode provider's simulated clocks, on `/Settings/Providers`.
	 *
	 * The renewal half of the store — renewals, dunning, a downgrade landing at period end, a trial
	 * ending — runs on a calendar, so in a test store it is otherwise observable only by waiting.
	 * A provider's test clock runs that calendar in seconds through the same code and webhooks.
	 * This panel creates clocks, advances them and deletes them; putting a **customer** on one is
	 * done from that customer's page, where the person being tested is named.
	 *
	 * Its own read, separately guarded, like every panel here: a provider that cannot list its
	 * clocks must not take the providers screen with it. Rendered only for test-mode rows — the
	 * server refuses a live one anyway.
	 */

	import { onDestroy } from 'svelte';
	import { Button, Input } from 'flowbite-svelte';
	import { ConfirmButton, EmptyState, Panel, RoutedLink, TestClockControls } from '../../../components';
	import { advanceTestClock, createTestClock, deleteTestClock, listTestClocks } from '../../../AppNetwork';
	import { anyClockAdvancing, CLOCK_POLL_MS, formatClockTime, fromLocalInputValue } from '../../../components/testClocks';
	import { toastError, toastSuccess } from '../../../stores/toast';
	import type { PaymentProviderConfig, TestClock, TestClockOverview } from '../../../AppTypes';

	export let provider: PaymentProviderConfig;

	let overview: TestClockOverview | null = null;
	let loading: boolean = true;
	let loadError: string = '';
	/** The clock a request is in flight for; `'new'` while one is being created. */
	let busyID: string = '';
	let newName: string = '';
	/** Empty means "now", which is what a clock almost always wants to start at. */
	let newStart: string = '';
	let pollTimer: ReturnType<typeof setTimeout> | undefined;

	$: void fnLoad(provider.uuid);

	async function fnLoad(providerUUID: string, quiet = false): Promise<void> {
		clearTimeout(pollTimer);
		if (!quiet) {
			loading = true;
		}
		loadError = '';
		try {
			overview = await listTestClocks(providerUUID);
			fnPollIfMoving();
		} catch (error) {
			loadError = (error as Error).message;
		} finally {
			loading = false;
		}
	}

	/** A clock still advancing is looked at again shortly, so the screen settles by itself. */
	function fnPollIfMoving(): void {
		if (overview !== null && anyClockAdvancing(overview.clocks)) {
			pollTimer = setTimeout(() => void fnLoad(provider.uuid, true), CLOCK_POLL_MS);
		}
	}

	function fnReplace(updated: TestClock): void {
		if (overview !== null) {
			overview = { ...overview, clocks: overview.clocks.map(clock => (clock.id === updated.id ? updated : clock)) };
		}
		fnPollIfMoving();
	}

	async function fnCreate(): Promise<void> {
		busyID = 'new';
		try {
			const name = newName.trim();
			const frozenTime = fromLocalInputValue(newStart);
			const created = await createTestClock(provider.uuid, {
				...(name === '' ? {} : { name }),
				...(frozenTime === null ? {} : { frozenTime })
			});
			if (overview !== null) {
				overview = { ...overview, clocks: [created, ...overview.clocks] };
			}
			newName = '';
			newStart = '';
			toastSuccess('Test clock created.', `It stands at ${formatClockTime(created.frozenTime)}.`);
		} catch (error) {
			toastError('The test clock was not created.', (error as Error).message);
		} finally {
			busyID = '';
		}
	}

	async function fnAdvance(clock: TestClock, frozenTime: string): Promise<void> {
		busyID = clock.id;
		try {
			fnReplace(await advanceTestClock(provider.uuid, clock.id, frozenTime));
		} catch (error) {
			toastError('The clock was not advanced.', (error as Error).message);
		} finally {
			busyID = '';
		}
	}

	async function fnDelete(clock: TestClock): Promise<void> {
		busyID = clock.id;
		try {
			const removal = await deleteTestClock(provider.uuid, clock.id);
			if (overview !== null) {
				overview = { ...overview, clocks: overview.clocks.filter(candidate => candidate.id !== clock.id) };
			}
			toastSuccess(
				'Test clock deleted.',
				removal.customersDetached.length === 0
					? ''
					: `${removal.customersDetached.length} customer${removal.customersDetached.length === 1 ? ' was' : 's were'} removed from the provider with it.`
			);
		} catch (error) {
			toastError('The test clock was not deleted.', (error as Error).message);
		} finally {
			busyID = '';
		}
	}

	onDestroy(() => clearTimeout(pollTimer));
</script>

<Panel title={`Test clocks — ${provider.name}`} faIcon="clock">
	{#if loading}
		<EmptyState variant="loading" title="Loading test clocks…" />
	{:else if loadError.length > 0}
		<EmptyState variant="error" title="The test clocks could not be loaded." description={loadError}>
			<Button color="alternative" size="sm" on:click={() => fnLoad(provider.uuid)}>Try again</Button>
		</EmptyState>
	{:else if overview !== null && !overview.supported}
		<EmptyState title="No simulated time on this provider." description={overview.reason ?? ''} />
	{:else if overview !== null}
		<p class="text-sm text-default mb-3">
			A clock runs renewals, retries, trial ends and cancellations for the customers on it as soon as it is
			advanced, and sends their webhooks. Put a customer on a clock from that customer's page. The provider
			deletes a clock, with its customers, 30 days after it was created.
		</p>

		<form class="flex flex-wrap items-end gap-2 mb-4" on:submit|preventDefault={fnCreate}>
			<div>
				<label class="block text-xs text-default mb-1" for={`new-clock-name-${provider.uuid}`}>Name</label>
				<Input id={`new-clock-name-${provider.uuid}`} size="sm" placeholder="Annual renewal" bind:value={newName} />
			</div>
			<div>
				<label class="block text-xs text-default mb-1" for={`new-clock-start-${provider.uuid}`}>Starts at (blank: now)</label>
				<Input id={`new-clock-start-${provider.uuid}`} type="datetime-local" size="sm" class="!w-auto" bind:value={newStart} />
			</div>
			<Button type="submit" size="sm" color="primary" disabled={busyID === 'new'}>
				{#if busyID === 'new'}
					<i class="fa-solid fa-spinner fa-spin mr-2" />
				{:else}
					<i class="fa-solid fa-plus mr-2" />
				{/if}
				New clock
			</Button>
		</form>

		{#if overview.clocks.length === 0}
			<EmptyState title="No test clocks." description="Create one, or put a customer on one from their page." />
		{:else}
			<div class="space-y-2">
				{#each overview.clocks as clock (clock.id)}
					<div class="widget bg-depth space-y-3" data-testid="test-clock">
						<div class="flex flex-wrap items-center gap-2">
							<span class="font-semibold">{clock.name ?? 'Unnamed clock'}</span>
							<code class="text-xs text-default">{clock.id}</code>
							<span class="grow" />
							<ConfirmButton
								size="xs"
								color="red"
								outline
								disabled={busyID === clock.id}
								confirmText={clock.customers.length === 0
									? `Delete test clock “${clock.name ?? clock.id}”?`
									: `Delete test clock “${clock.name ?? clock.id}”? The provider deletes the ${clock.customers.length} customer${clock.customers.length === 1 ? '' : 's'} on it too, and cancels their subscriptions.`}
								on:confirm={() => fnDelete(clock)}
							>
								<i class="fa-solid fa-trash mr-1" />Delete
							</ConfirmButton>
						</div>

						<TestClockControls {clock} busy={busyID === clock.id} on:advance={event => fnAdvance(clock, event.detail.frozenTime)} />

						<div class="text-xs text-default">
							{#if clock.customers.length === 0}
								No customers on this clock.
							{:else}
								On this clock:
								{#each clock.customers as customer, index (customer.providerCustomerID)}
									{#if index > 0}, {/if}
									{#if customer.userUUID !== null}
										<RoutedLink to={`/Customers/${customer.userUUID}`} class="text-primary-600 hover:underline">
											{customer.displayName ?? customer.email ?? customer.userUUID}
										</RoutedLink>
									{:else}
										<code>{customer.providerCustomerID}</code>
									{/if}
								{/each}
							{/if}
						</div>
					</div>
				{/each}
			</div>
		{/if}
	{/if}
</Panel>
